import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import TabsWrapper from "../../shared/TabsWrapper/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import CreateCustomerDialog from "./CreateCustomerDialog.tsx";

interface Customer {
  id: number;
  name: string;
  phone: string;
  email: string;
  customerType: string;
  noOfWashes: number;
  status: string;
}

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CustomersList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: 0, title: null as string | null });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    getCustomers();
  }, [page, limit, search, tabValue]);

  const getCustomers = async () => {
    setLoading(true);
    const showPendingPayment = tabValue === "pending" ? "true" : "false";
    const url = `${API_BASE}/api/getCustomers?showPendingPayment=${showPendingPayment}`;
    const response = await request.get(search ? `${url}&search=${search}` : url);
    setCustomers(response.data.data);
    setTotalCount(response.data.count);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "name",
      label: "Name",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "phone",
      label: "Phone",
      minWidth: 130,
      format: (value) => value,
    },
    {
      id: "email",
      label: "Email",
      minWidth: 180,
      format: (value) => value || "-",
    },
    {
      id: "customerType",
      label: "Customer Type",
      minWidth: 120,
      format: (value) => value || "-",
    },
    {
      id: "noOfWashes",
      label: "No Of Washes",
      minWidth: 100,
      format: (value) => value ?? 0,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      format: (_value, row) => (
        <ActionIcons actions={[
          { icon: <EditIcon />, tooltip: "Edit", onClick: () => navigate(`/admin/customers/${row.id}`) },
          // { icon: <DeleteIcon />, isDelete: true, tooltip: "Delete", onClick: () => setDeleteDialog({ open: true, id: row.id, title: row.name }) },
        ]} />
      ),
    },
  ];

  const dataTable = (
    <DataTable
      columns={columns}
      rows={customers}
      totalCount={totalCount}
      onPaginationChange={handlePaginationChange}
      page={page}
      limit={limit}
    />
  );

  return (
    <PageWrapper loading={loading}>
      <PageHeader
        title="Customers"
        showSearch
        searchPlaceholder="Search customers..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Customer",
          icon: <AddIcon />,
          onClick: () => setCreateDialogOpen(true),
        }}
      />
      <TabsWrapper
        defaultValue={tabValue}
        tabValueChange={(v) => { setPage(0); setTabValue(v); }}
        tabs={[
          { label: "All Customers", value: "all", content: dataTable },
          { label: "Pending payment customers", value: "pending", content: dataTable },
        ]}
      />
      <DeleteConfirmation
        open={deleteDialog.open}
        title={"Delete - " + deleteDialog?.title}
        message="Are you sure you want to delete this customer?"
        apiEndpoint={"api/deleteCustomer/"}
        deleteId={deleteDialog.id}
        onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
        refetchApi={() => getCustomers()}
      />
      <CreateCustomerDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => getCustomers()}
      />
    </PageWrapper>
  );
}
